// Portfolio Administration v1.0 PR 6 §10.3 — propose-and-approve workflow
// buttons. Render only the action buttons appropriate to the selected
// config's status and the current user's permissions. Self-approval is
// prevented client-side as a UX courtesy; the backend rejects it too.

import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import {
  useApproveWaterfallDraft,
  useProposeWaterfallDraft,
  useRejectWaterfallDraft,
} from "@/hooks/portfolioAdmin/usePr6";
import type { WaterfallConfigDto } from "@/api/portfolioAdmin/pr6Types";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface ProposeApproveRejectButtonsProps {
  waterfallCode: string;
  config: WaterfallConfigDto;
}

export function ProposeApproveRejectButtons({
  waterfallCode,
  config,
}: ProposeApproveRejectButtonsProps) {
  const auth = useAuth();
  const canEdit =
    auth.hasPermission("Waterfall.ConfigEdit") || auth.hasRoleCode("ADMIN");
  const canApprove =
    auth.hasPermission("Waterfall.ConfigApprove") || auth.hasRoleCode("ADMIN");
  const canSelfApprove =
    auth.hasPermission("Waterfall.SelfApprove") || auth.hasRoleCode("ADMIN");

  const proposeMutation = useProposeWaterfallDraft(waterfallCode);
  const approveMutation = useApproveWaterfallDraft(waterfallCode);
  const rejectMutation = useRejectWaterfallDraft(waterfallCode);
  const tz = useUserTimeZone();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(
    isoDateInTz(new Date(), tz),
  );
  const [rejectReason, setRejectReason] = useState("");

  const sameUserAsProposer =
    !!config.proposedByUserId &&
    !!auth.user?.id &&
    config.proposedByUserId === auth.user.id;
  const blockedBySelfApprove = sameUserAsProposer && !canSelfApprove;

  if (config.status === "draft" && canEdit) {
    return (
      <Button
        type="button"
        variant="primary"
        size="sm"
        disabled={proposeMutation.isPending}
        onClick={() => proposeMutation.mutate(config.id)}
        data-testid="propose-for-approval-button"
      >
        {proposeMutation.isPending ? "Proposing…" : "Propose for approval"}
      </Button>
    );
  }

  if (config.status === "proposed") {
    return (
      <div className="flex items-center gap-space-2">
        {canApprove && (
          <>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setApproveOpen(true)}
              disabled={blockedBySelfApprove}
              title={
                blockedBySelfApprove
                  ? "Self-approval is not allowed — a different user must approve."
                  : undefined
              }
              data-testid="approve-and-activate-button"
            >
              Approve and activate
            </Button>
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => setRejectOpen(true)}
              data-testid="reject-proposal-button"
            >
              Reject
            </Button>
          </>
        )}
        <Modal
          open={approveOpen}
          onClose={() => !approveMutation.isPending && setApproveOpen(false)}
          dismissOnBackdropClick={false}
          title={`Approve ${waterfallCode} v${config.version}`}
          description="Sets the new active version's effective start date and supersedes any prior active version on that date."
          footer={
            <>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => setApproveOpen(false)}
                disabled={approveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={approveMutation.isPending || !effectiveDate}
                onClick={() => {
                  approveMutation.mutate(
                    {
                      configId: config.id,
                      body: { effectiveStartDate: effectiveDate },
                    },
                    {
                      onSuccess: () => setApproveOpen(false),
                    },
                  );
                }}
                data-testid="approve-confirm"
              >
                {approveMutation.isPending ? "Activating…" : "Activate"}
              </Button>
            </>
          }
        >
          <label className="text-small font-bold block mb-space-1">
            Effective start date
          </label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="border border-border-light rounded-md p-space-2 text-small w-full"
            data-testid="approve-effective-date"
          />
          {approveMutation.isError && (
            <p className="text-small text-status-error m-0 mt-space-2">
              {approveMutation.error instanceof Error
                ? approveMutation.error.message
                : "Approval failed"}
            </p>
          )}
        </Modal>
        <Modal
          open={rejectOpen}
          onClose={() => !rejectMutation.isPending && setRejectOpen(false)}
          dismissOnBackdropClick={false}
          title={`Reject ${waterfallCode} v${config.version}`}
          description="Reverts the proposed version back to draft. The reason is recorded in the change-justification audit trail."
          footer={
            <>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => setRejectOpen(false)}
                disabled={rejectMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={
                  rejectMutation.isPending || rejectReason.trim().length < 3
                }
                onClick={() =>
                  rejectMutation.mutate(
                    {
                      configId: config.id,
                      body: { reason: rejectReason },
                    },
                    { onSuccess: () => setRejectOpen(false) },
                  )
                }
                data-testid="reject-confirm"
              >
                {rejectMutation.isPending ? "Rejecting…" : "Reject"}
              </Button>
            </>
          }
        >
          <label className="text-small font-bold block mb-space-1">
            Rejection reason
          </label>
          <textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="w-full border border-border-light rounded-md p-space-2 text-small"
            data-testid="reject-reason"
          />
        </Modal>
      </div>
    );
  }

  return null;
}
